import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { authApi } from '@/lib/api/auth'
import { useAuthStore } from '@/store/authStore'
import { Alert, AlertDescription } from '@/components/ui/alert'

export default function MicrosoftCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [error, setError] = useState('')

  useEffect(() => {
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const msError = searchParams.get('error')

    if (msError) {
      const desc = searchParams.get('error_description') ?? msError
      setError(`Microsoft sign-in was cancelled or failed: ${desc}`)
      return
    }

    if (!code || !state) {
      setError('Invalid callback — missing code or state. Please try again.')
      return
    }

    const savedState = sessionStorage.getItem('ms_oauth_state')
    if (!savedState || savedState !== state) {
      setError('Security check failed. Please sign in again.')
      return
    }
    sessionStorage.removeItem('ms_oauth_state')

    // Ensure device ID exists — should have been set on LoginPage before redirect
    let deviceId = localStorage.getItem('portal_device_id')
    if (!deviceId) {
      deviceId = crypto.randomUUID()
      localStorage.setItem('portal_device_id', deviceId)
    }

    authApi.microsoftCallback(code, state, deviceId).then((res) => {
      if (!res.success || !res.data) {
        const code = (res as any).code as string | undefined
        if (code === 'DEVICE_REJECTED') {
          setError('Device access denied. Contact your administrator.')
        } else {
          setError(res.error ?? 'Sign-in failed. Please try again.')
        }
        return
      }
      const data = res.data
      if (data.requiresDeviceApproval) {
        navigate('/device-pending', { replace: true })
        return
      }
      if (data.requiresTwoFA) {
        navigate(`/login?totp=${data.userId}`, { replace: true })
        return
      }
      setAuth(data.user!, data.accessToken!, data.refreshToken!, data.modules ?? [])
      navigate(
        (data.user as any)?.mustChangePassword ? '/change-password' : '/dashboard',
        { replace: true },
      )
    }).catch((err: any) => {
      if (err?.code === 'DEVICE_REJECTED') {
        setError('Device access denied. Contact your administrator.')
      } else {
        setError(err?.error ?? err?.message ?? 'Sign-in failed. Please try again.')
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#0B2545] flex items-center justify-center p-4">
      <div className="w-full max-w-md flex flex-col items-center gap-6">
        <img src="/logo.png" alt="Monarch Innovation" className="h-12 w-auto" />

        {error ? (
          <div className="w-full space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-slate-300 hover:text-white transition-colors underline underline-offset-4"
              >
                ← Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-white/60" />
            <p className="text-slate-400 text-sm">Signing you in…</p>
          </div>
        )}
      </div>
    </div>
  )
}
